import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import ServerList from './ServerList';
import ChannelList from './ChannelList';
import ChatArea from './ChatArea';
import MemberList from './MemberList';
import ServerSetup from './ServerSetup';
import VoiceChannel from './VoiceChannel';
import Friends from './Friends';
import DMList from './DMList';
import AdminDashboard from './AdminDashboard';
import GamingHub from './GamingHub';
import { useNotification } from './NotificationSystem';
import { API_URL } from '../config';
import './MainApp.css';

console.log('API URL:', API_URL);

function MainApp({ token, user, onLogout }) {
  const { showSuccess, showError, showInfo, confirm } = useNotification();
  const [socket, setSocket] = useState(null);
  const [servers, setServers] = useState([]);
  const [currentServer, setCurrentServer] = useState(null);
  const [channels, setChannels] = useState([]);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [connectedVoiceChannel, setConnectedVoiceChannel] = useState(null); // Track voice connection separately
  const [messages, setMessages] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [serverMembers, setServerMembers] = useState([]); // All members (online + offline)
  const [showServerSetup, setShowServerSetup] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({}); // channelId -> unread count
  const [mentionCounts, setMentionCounts] = useState({}); // channelId -> mention count
  const [voiceUsers, setVoiceUsers] = useState({}); // channelId -> array of users
  const [voiceAudioControls, setVoiceAudioControls] = useState(null); // Controls from VoiceChannel
  const [viewMode, setViewMode] = useState('server'); // 'server' or 'home'
  const [showFriends, setShowFriends] = useState(false); // Show friends list in home view
  const [dmChannels, setDmChannels] = useState([]); // List of DM channels
  const [friends, setFriends] = useState([]); // List of friends
  const [pendingFriendRequests, setPendingFriendRequests] = useState([]); // Pending friend requests
  const [channelPermissions, setChannelPermissions] = useState({ 
    VIEW_CHANNEL: true, 
    SEND_MESSAGES: true, 
    EDIT_MESSAGES: true,
    ADD_REACTIONS: true,
    CONNECT_VOICE: true,
    SPEAK: true
  }); // Current channel permissions - default to allowing basic actions
  const [showMobileServerList, setShowMobileServerList] = useState(false);
  const [showMobileChannelList, setShowMobileChannelList] = useState(false);
  const [showMobileMemberList, setShowMobileMemberList] = useState(false);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showGamingHub, setShowGamingHub] = useState(false);
  const currentChannelRef = useRef(null);
  const currentServerRef = useRef(null);

  // Check if current user is admin
  const isAdmin = user && user.email === 'eoothewise@gmail.com';

  useEffect(() => {
    // Initialize socket connection
    const newSocket = io(API_URL, {
      auth: { token }
    });

    newSocket.on('connect', () => {
      console.log('Connected to server');
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      // If authentication error, logout and redirect
      if (error.message.includes('Authentication') || error.message.includes('User not found')) {
        showError('Your session has expired. Please login again.');
        onLogout();
      }
    });

    newSocket.on('server_shutdown', ({ message }) => {
      console.log('Server shutdown notification:', message);
      // Disconnect from voice if connected
      setConnectedVoiceChannel(null);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Disconnected from server:', reason);
      // Clean up voice connection on unexpected disconnect
      if (reason === 'transport close' || reason === 'transport error') {
        setConnectedVoiceChannel(null);
      }
    });

    newSocket.on('reconnect', (attemptNumber) => {
      console.log('Reconnected to server after', attemptNumber, 'attempts');
      // Reload data after reconnection
      fetchServers();
      fetchOnlineUsers();
      fetchDMChannels();
      fetchFriends();
      fetchPendingFriendRequests();
    });

    newSocket.on('user_status', (data) => {
      setOnlineUsers(prev => {
        const filtered = prev.filter(u => u.id !== data.userId);
        if (data.status !== 'offline') {
          return [...filtered, { id: data.userId, username: data.username, status: data.status, avatar_url: data.avatar_url }];
        }
        return filtered;
      });
      
      // Also update server members list if applicable
      setServerMembers(prev => prev.map(member => 
        member.id === data.userId 
          ? { ...member, status: data.status, avatar_url: data.avatar_url }
          : member
      ));
    });

    newSocket.on('user_typing', (data) => {
      // Handle typing indicator
      console.log(`${data.username} is typing...`);
    });

    newSocket.on('unread_update', (unreads) => {
      setUnreadCounts(unreads);
    });

    newSocket.on('mention_update', (mentions) => {
      setMentionCounts(mentions);
    });

    newSocket.on('server_member_update', (data) => {
      // Update specific server's member list
      setServers(prev => prev.map(server => 
        server.id === data.serverId 
          ? { ...server, members: data.members }
          : server
      ));
    });

    newSocket.on('voice_users', ({ channelId, users }) => {
      setVoiceUsers(prev => ({ ...prev, [channelId]: users }));
    });

    newSocket.on('user_joined_voice', ({ userId, username, channelId }) => {
      setVoiceUsers(prev => {
        const channelUsers = prev[channelId] || [];
        // Prevent duplicates
        if (channelUsers.some(u => u.userId === userId)) {
          return prev;
        }
        return {
          ...prev,
          [channelId]: [...channelUsers, { userId, username }]
        };
      });
    });

    newSocket.on('user_left_voice', ({ userId, channelId }) => {
      setVoiceUsers(prev => ({
        ...prev,
        [channelId]: (prev[channelId] || []).filter(u => u.userId !== userId)
      }));
    });

    // Handle friend request events
    newSocket.on('friend_request_received', (request) => {
      setPendingFriendRequests(prev => [...prev, request]);
    });

    newSocket.on('friend_added', (friend) => {
      setFriends(prev => [...prev, friend]);
      setPendingFriendRequests(prev => prev.filter(r => r.from_user_id !== friend.id));
    });

    newSocket.on('friend_removed', ({ friendId }) => {
      setFriends(prev => prev.filter(f => f.id !== friendId));
    });

    // Handle real-time channel updates
    newSocket.on('channel_created', (channel) => {
      console.log('➕ Channel created event:', channel);
      
      setServers(prev => prev.map(server => {
        if (server.id === channel.server_id) {
          return {
            ...server,
            channels: [...(server.channels || []), channel]
          };
        }
        return server;
      }));
      
      // Update channels array if it's for the currently selected server
      if (currentServerRef.current === channel.server_id) {
        setChannels(prev => [...prev, channel]);
      }
    });

    newSocket.on('channel_updated', (channel) => {
      console.log('Channel updated event:', channel);
      
      setServers(prev => prev.map(server => {
        if (server.id === channel.server_id) {
          return {
            ...server,
            channels: (server.channels || []).map(c => 
              c.id === channel.id ? channel : c
            )
          };
        }
        return server;
      }));
      
      // Also update the channels array
      setChannels(prev => prev.map(c => 
        c.id === channel.id ? channel : c
      ));
    });

    newSocket.on('channel_deleted', (channelId) => {
      console.log('Channel deleted event:', channelId);
      
      setServers(prev => prev.map(server => ({
        ...server,
        channels: (server.channels || []).filter(c => c.id !== channelId)
      })));
      
      // Also remove from channels array
      setChannels(prev => prev.filter(c => c.id !== channelId));
      
      // Clear current channel if it was deleted using state updater
      setCurrentChannel(prev => {
        if (prev?.id === channelId) {
          return null;
        }
        return prev;
      });
    });

    // Handle server deletion
    newSocket.on('server_deleted', ({ serverId, message }) => {
      console.log('🗑️ Server deleted event:', serverId);
      
      // Show alert to user
      showInfo(message || 'This server has been deleted');
      
      // Remove server from list
      setServers(prev => prev.filter(s => s.id !== serverId));
      
      // If the deleted server was selected, switch to home/DMs
      if (currentServer === serverId) {
        setCurrentServer(null);
        setCurrentChannel(null);
        setChannels([]);
        setServerMembers([]);
        setViewMode('home'); // Switch to home view
      }
    });

    // Handle voice disconnect (when server is deleted or kicked)
    newSocket.on('voice_disconnected', ({ channelId, reason }) => {
      console.log('Voice disconnected:', channelId, reason);
      showInfo(`Disconnected from voice: ${reason}`);
      // VoiceChannel component will handle cleanup through its own socket listeners
    });

    // Handle channel reordering
    newSocket.on('channels_reordered', (reorderedChannels) => {
      console.log('Channels reordered:', reorderedChannels);
      setChannels(reorderedChannels);
    });

    setSocket(newSocket);

    // Fetch initial data
    fetchServers();
    fetchOnlineUsers();    fetchDMChannels();
    fetchFriends();
    fetchPendingFriendRequests();
    return () => {
      newSocket.disconnect();
    };
  }, [token]);

  // Separate effect for handling new messages with current channel
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (message) => {
      console.log('Received new_message event:', message);
      console.log('Current channel:', currentChannel);
      console.log('Message channelId:', message.channelId, 'Current channel id:', currentChannel?.id);
      console.log('Match:', message.channelId === currentChannel?.id);
      
      if (message.channelId === currentChannel?.id) {
        console.log('Message is for current channel, adding to messages');
        // Ensure message has reactions object to prevent them from disappearing
        const messageWithReactions = {
          ...message,
          reactions: message.reactions || {}
        };
        setMessages(prev => {
          console.log('Adding message to state. Previous count:', prev.length);
          return [...prev, messageWithReactions];
        });
      } else {
        console.log('Message is for a different channel, ignoring');
      }
    };

    const handleMessageEdited = ({ messageId, content, editedAt }) => {
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, content, editedAt } 
          : msg
      ));
    };

    const handleMessageDeleted = ({ messageId }) => {
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
    };

    const handleUserStatus = ({ userId, username, status }) => {
      setOnlineUsers(prev => {
        if (status === 'online') {
          // Add or update user
          const exists = prev.find(u => u.id === userId);
          if (exists) {
            return prev.map(u => u.id === userId ? { ...u, status } : u);
          }
          return [...prev, { id: userId, username, status }];
        } else {
          // Remove user when offline
          return prev.filter(u => u.id !== userId);
        }
      });
      
      // Update serverMembers status as well
      setServerMembers(prev => 
        prev.map(member => 
          member.id === userId ? { ...member, status } : member
        )
      );
    };

    console.log('Setting up socket listeners for channel:', currentChannel?.id);
    socket.on('new_message', handleNewMessage);
    socket.on('message_edited', handleMessageEdited);
    socket.on('message_deleted', handleMessageDeleted);
    socket.on('user_status', handleUserStatus);

    return () => {
      console.log('Removing socket listeners for channel:', currentChannel?.id);
      socket.off('new_message', handleNewMessage);
      socket.off('message_edited', handleMessageEdited);
      socket.off('message_deleted', handleMessageDeleted);
      socket.off('user_status', handleUserStatus);
    };
  }, [socket, currentChannel]);

  useEffect(() => {
    if (currentServer) {
      currentServerRef.current = currentServer.id;
      fetchChannels(currentServer.id);
      fetchServerMembers(currentServer.id);
    } else {
      currentServerRef.current = null;
      setServerMembers([]);
    }
  }, [currentServer]);

  useEffect(() => {
    if (currentChannel && socket) {
      // Prevent duplicate joins for the same channel
      if (currentChannelRef.current === currentChannel.id) {
        return;
      }
      
      // Leave previous channel if any
      if (currentChannelRef.current) {
        console.log('Leaving channel:', currentChannelRef.current);
        socket.emit('leave_channel', currentChannelRef.current);
      }
      
      // Join new channel
      console.log('Joining channel:', currentChannel.id);
      socket.emit('join_channel', currentChannel.id);
      currentChannelRef.current = currentChannel.id;
      fetchMessages(currentChannel.id);
      
      // Fetch channel permissions if it's not a DM
      if (!currentChannel.is_dm) {
        fetchChannelPermissions(currentChannel.id);
      }
    }
  }, [currentChannel]); // Only depend on currentChannel, not socket

  const fetchServers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/servers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServers(response.data);
      if (response.data.length > 0) {
        setCurrentServer(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching servers:', error);
      // Handle authentication errors
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        showError('Your session has expired. Please login again.');
        onLogout();
      }
    }
  };

  const fetchChannels = async (serverId) => {
    try {
      const response = await axios.get(`${API_URL}/api/servers/${serverId}/channels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChannels(response.data);
      if (response.data.length > 0) {
        setCurrentChannel(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching channels:', error);
      // Handle authentication errors
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        showError('Your session has expired. Please login again.');
        onLogout();
      }
    }
  };

  const fetchServerMembers = async (serverId) => {
    try {
      const response = await axios.get(`${API_URL}/api/servers/${serverId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Transform the data to match expected format
      const members = response.data.map(member => ({
        id: member.user_id,
        username: member.username,
        status: member.status || 'offline',
        avatar_url: member.avatar_url,
        roles: member.roles || []
      }));
      setServerMembers(members);
    } catch (error) {
      console.error('Error fetching server members:', error);
    }
  };

  const fetchChannelPermissions = async (channelId) => {
    try {
      const response = await axios.get(`${API_URL}/api/channels/${channelId}/user-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Channel permissions response:', response.data);
      setChannelPermissions(response.data);
    } catch (error) {
      console.error('Error fetching channel permissions:', error);
      // Default to basic permissions if fetch fails - allow everything by default
      setChannelPermissions({ 
        VIEW_CHANNEL: true, 
        SEND_MESSAGES: true, 
        EDIT_MESSAGES: true,
        DELETE_MESSAGES: false,
        ADD_REACTIONS: true,
        CONNECT_VOICE: true,
        SPEAK: true
      });
    }
  };

  const fetchMessages = async (channelId) => {
    try {
      // Check if this is a DM channel by looking at currentChannel type or checking dmChannels
      const isDM = currentChannel?.type === 'dm' || dmChannels.some(dm => dm.id === channelId);
      console.log('Fetching messages for channel:', channelId, 'isDM:', isDM);
      const endpoint = isDM 
        ? `${API_URL}/api/dms/${channelId}/messages`
        : `${API_URL}/api/channels/${channelId}/messages`;
      
      console.log('Using endpoint:', endpoint);
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fetched', response.data.length, 'messages');
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchDMChannels = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDmChannels(response.data);
    } catch (error) {
      console.error('Error fetching DM channels:', error);
    }
  };

  const fetchFriends = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/friends`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriends(response.data);
    } catch (error) {
      console.error('Error fetching friends:', error);
    }
  };

  const fetchPendingFriendRequests = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/friends/requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingFriendRequests(response.data);
    } catch (error) {
      console.error('Error fetching pending friend requests:', error);
    }
  };

  const createDM = async (friendId) => {
    try {
      const response = await axios.post(`${API_URL}/api/dms/create`, 
        { friendId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newDM = response.data;
      
      // Add to DM list if not already there
      setDmChannels(prev => {
        if (prev.some(dm => dm.id === newDM.id)) {
          return prev;
        }
        return [newDM, ...prev];
      });
      
      // Switch to home view and select this DM
      setViewMode('home');
      setShowFriends(false);
      setCurrentChannel({ 
        id: newDM.id, 
        name: newDM.recipient?.username || newDM.recipient_username, 
        type: 'dm' 
      });
      
      return newDM;
    } catch (error) {
      console.error('Error creating DM:', error);
      showError('Failed to create DM');
    }
  };

  const closeDM = (dmId, e) => {
    if (e) {
      e.stopPropagation();
    }
    // Remove from visible list (history retained in DB)
    setDmChannels(prev => prev.filter(dm => dm.id !== dmId));
    
    // If currently viewing this DM, clear selection
    if (currentChannel?.id === dmId) {
      setCurrentChannel(null);
      setMessages([]);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/online`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOnlineUsers(response.data);
    } catch (error) {
      console.error('Error fetching online users:', error);
    }
  };

  const sendMessage = (content) => {
    if (socket && currentChannel) {
      console.log('Sending message to channel:', currentChannel.id, 'Content:', content);
      socket.emit('send_message', {
        channelId: currentChannel.id,
        content
      });
    } else {
      console.error('Cannot send message: socket or currentChannel missing', { socket: !!socket, currentChannel });
    }
  };

  const handleLogout = () => {
    // Leave current channel if any
    if (socket && currentChannelRef.current) {
      socket.emit('leave_channel', currentChannelRef.current);
    }
    
    // Disconnect socket
    if (socket) {
      socket.disconnect();
    }
    
    // Clear state
    setCurrentServer(null);
    setCurrentChannel(null);
    setChannels([]);
    setMessages([]);
    setOnlineUsers([]);
    setVoiceUsers({});
    
    // Call parent logout to clear localStorage
    onLogout();
  };

  const handleServerCreated = async (newServer) => {
    // Refetch servers to get updated member lists
    await fetchServers();
    setCurrentServer(newServer);
    setShowServerSetup(false);
  };

  const handleUserContextMenu = async (action, targetUser, extraData) => {
    try {
      switch (action) {
        case 'profile':
          // Profile modal will be handled by the component itself
          break;
        case 'message':
          await createDM(targetUser.id);
          break;
        case 'invite':
          // TODO: Implement server invite functionality
          const serverId = extraData;
          console.log('Invite user', targetUser.id, 'to server', serverId);
          showInfo('Server invites not yet implemented');
          break;
        case 'addFriend':
          const response = await axios.post(`${API_URL}/api/friends/request`,
            { username: targetUser.username },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          showSuccess(response.data.message || 'Friend request sent');
          break;
        case 'block':
          const blockConfirmed = await confirm(`Are you sure you want to block ${targetUser.username}?`, 'Block User');
          if (blockConfirmed) {
            await axios.post(`${API_URL}/api/users/block`,
              { userId: targetUser.id },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            showSuccess('User blocked');
          }
          break;
        default:
          console.log('Unknown context menu action:', action);
      }
    } catch (error) {
      console.error('Error handling context menu action:', error);
      showError(error.response?.data?.error || 'Action failed');
    }
  };

  // Show server setup screen if user has no servers or clicked add button
  if (servers.length === 0 || showServerSetup) {
    return <ServerSetup token={token} onServerCreated={handleServerCreated} onLogout={handleLogout} />;
  }

  // serverMembers is now fetched from API and includes all members (online + offline)

  return (
    <div className="main-app">
      {/* Mobile overlay backdrop */}
      {(showMobileServerList || showMobileChannelList || showMobileMemberList) && (
        <div 
          className="mobile-overlay" 
          onClick={() => {
            setShowMobileServerList(false);
            setShowMobileChannelList(false);
            setShowMobileMemberList(false);
          }}
        />
      )}
      
      <ServerList 
        servers={servers} 
        currentServer={currentServer}
        onSelectServer={(server) => {
          setViewMode('server');
          setCurrentServer(server);
        }}
        onAddServer={() => setShowServerSetup(true)}
        onHomeClick={() => {
          setViewMode('home');
          setShowFriends(true);
          setShowMobileServerList(false);
          setShowMobileChannelList(false);
        }}
        showMobile={showMobileServerList}
        pendingFriendRequestsCount={pendingFriendRequests.length}
        dmUnreadCount={dmChannels.reduce((total, dm) => total + (unreadCounts[dm.id] || 0), 0)}
        isAdmin={isAdmin}
        onAdminClick={() => setShowAdminDashboard(true)}
        onGamingHubClick={() => setShowGamingHub(true)}
      />
      
      {viewMode === 'server' ? (
        <>
          <ChannelList 
            server={currentServer}
            channels={channels}
            currentChannel={currentChannel}
            onSelectChannel={(channel) => {
              setCurrentChannel(channel);
              setShowMobileServerList(false);
              setShowMobileChannelList(false);
            }}
            user={user}
            onLogout={handleLogout}
            unreadCounts={unreadCounts}
            mentionCounts={mentionCounts}
            voiceUsers={voiceUsers}
            socket={socket}
            voiceAudioControls={voiceAudioControls}
            showMobile={showMobileChannelList}
            onToggleMobileMenu={() => setShowMobileServerList(!showMobileServerList)}
          />
          {/* Single VoiceChannel instance - persists when connected even if viewing text */}
          {(currentChannel?.type === 'voice' || connectedVoiceChannel) && (
            <VoiceChannel 
              key={(connectedVoiceChannel || currentChannel)?.id}
              socket={socket}
              channel={connectedVoiceChannel || currentChannel}
              user={user}
              onConnect={() => setConnectedVoiceChannel(currentChannel)}
              onDisconnect={() => setConnectedVoiceChannel(null)}
              isVisible={currentChannel?.type === 'voice'}
              onAudioControlsReady={setVoiceAudioControls}
            />
          )}
          {/* Show ChatArea when viewing text channel */}
          {currentChannel?.type === 'text' && (
            <ChatArea 
              channel={currentChannel}
              messages={messages}
              onSendMessage={sendMessage}
              user={user}
              socket={socket}
              members={serverMembers}
              servers={servers}
              currentServer={currentServer}
              token={token}
              onCreateDM={createDM}
              friends={friends}
              userPermissions={channelPermissions}
              onToggleMobileServerList={() => setShowMobileServerList(!showMobileServerList)}
              onToggleMobileChannelList={() => setShowMobileChannelList(!showMobileChannelList)}
              onToggleMobileMemberList={() => setShowMobileMemberList(!showMobileMemberList)}
            />
          )}
          <MemberList 
            members={serverMembers}
            currentUser={user}
            onUserContextMenu={handleUserContextMenu}
            friends={friends}
            showMobile={showMobileMemberList}
          />
        </>
      ) : (
        <>
          <DMList 
            dmChannels={dmChannels}
            currentChannel={currentChannel}
            onSelectDM={(dm) => {
              console.log('📧 Selecting DM:', dm);
              setShowFriends(false);
              setCurrentChannel({
                id: dm.id,
                name: dm.recipient_username || dm.recipient?.username,
                type: 'dm'
              });
            }}
            onCloseDM={closeDM}
            onShowFriends={() => setShowFriends(true)}
            user={user}
          />
          {showFriends ? (
            <Friends 
              token={token}
              user={user}
              socket={socket}
              onStartDM={createDM}
            />
          ) : (
            currentChannel && (
              <ChatArea 
                channel={currentChannel}
                messages={messages}
                onSendMessage={sendMessage}
                user={user}
                socket={socket}
                members={[]} // DMs don't have members list
                servers={servers}
                token={token}
                onCreateDM={createDM}
                friends={friends}
                userPermissions={{ SEND_MESSAGES: true, EDIT_MESSAGES: true, DELETE_MESSAGES: false }} // DMs have basic permissions
              />
            )
          )}
        </>
      )}

      {showServerSetup && (
        <ServerSetup 
          token={token} 
          onClose={() => setShowServerSetup(false)} 
          onServerCreated={handleServerCreated}
        />
      )}

      {showAdminDashboard && (
        <AdminDashboard
          token={token}
          onClose={() => setShowAdminDashboard(false)}
        />
      )}

      {showGamingHub && (
        <GamingHub
          user={user}
          servers={servers}
          socket={socket}
          onClose={() => setShowGamingHub(false)}
          onJoinServer={(server) => {
            setCurrentServer(server);
            setViewMode('server');
            setShowGamingHub(false);
          }}
        />
      )}
    </div>
  );
}

export default MainApp;
